import { useState, useRef, useEffect } from 'react';
import {
	ChevronDownIcon,
	CheckIcon,
	PlusIcon,
	BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import { useLocationModal } from '../../contexts';
import useProviderProfileStore from '../../stores/providerProfileStore';
import type { ProviderLocation } from '../../types/provider';

// Helper to get initials from location name (returns null if name is empty)
const getInitials = (name: string | null | undefined): string | null => {
	if (!name || name.trim() === '') return null;
	const initials = name
		.trim()
		.split(' ')
		.filter(word => word.length > 0)
		.map(word => word[0])
		.join('')
		.toUpperCase()
		.slice(0, 2);
	return initials || null;
};

// Helper to format location address
const formatAddress = (location: ProviderLocation): string => {
	const parts = [location.address, location.city].filter(Boolean);
	return parts.join(', ');
};

// Color variations for different locations
const locationColors = [
	'from-[#667eea] to-[#764ba2]',
	'from-amber-500 to-amber-600',
	'from-emerald-500 to-emerald-600',
	'from-rose-500 to-rose-600',
	'from-cyan-500 to-cyan-600',
];

const LocationSwitcher = () => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const { openAddLocation } = useLocationModal();

	const { profile, selectedLocationId, setSelectedLocation, getSelectedLocation } =
		useProviderProfileStore();

	const selectedLocation = getSelectedLocation();
	const locations = profile?.locations || [];

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleLocationSelect = (location: ProviderLocation) => {
		setSelectedLocation(location.id);
		setIsOpen(false);
	};

	const handleAddLocation = () => {
		setIsOpen(false);
		openAddLocation();
	};

	// Get color for location based on index
	const getLocationColor = (index: number): string => {
		return locationColors[index % locationColors.length];
	};

	// If no locations, show a placeholder
	if (locations.length === 0) {
		return (
			<button
				onClick={handleAddLocation}
				className="flex items-center gap-3 px-4 py-2 bg-white/15 hover:bg-white/25 rounded-xl transition-all cursor-pointer"
			>
				<div className="w-10 h-10 border-2 border-dashed border-white/50 rounded-[10px] flex items-center justify-center">
					<PlusIcon className="w-5 h-5 text-white/70" />
				</div>
				<div className="text-left hidden sm:block">
					<div className="font-bold text-sm text-white">Add Location</div>
					<div className="text-xs text-white/85">Set up your first location</div>
				</div>
			</button>
		);
	}

	return (
		<div className="relative" ref={dropdownRef}>
			{/* Current Location Button */}
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="flex items-center gap-3 px-4 py-2 bg-white/15 hover:bg-white/25 rounded-xl transition-all cursor-pointer"
			>
				{/* Location Avatar */}
				<div
					className={`w-10 h-10 bg-gradient-to-br ${getLocationColor(0)} rounded-[10px] flex items-center justify-center font-bold text-sm text-white flex-shrink-0`}
				>
					{getInitials(profile?.businessName) || (
						<BuildingStorefrontIcon className="w-5 h-5" />
					)}
				</div>

				{/* Location Info */}
				<div className="text-left hidden sm:block">
					<div className="font-bold text-base text-white">
						{profile?.businessName || 'Select Location'}
					</div>
					<div className="text-xs text-white/85">
						{selectedLocation
							? formatAddress(selectedLocation)
							: 'No location selected'}
					</div>
				</div>

				{/* Dropdown Arrow */}
				<ChevronDownIcon
					className={`w-4 h-4 text-white/80 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
				/>
			</button>

			{/* Dropdown */}
			<div
				className={`
					absolute top-[calc(100%+10px)] right-0 w-80 bg-white dark:bg-gray-800 rounded-2xl
					shadow-[0_10px_40px_rgba(0,0,0,0.2)] overflow-hidden z-[1000]
					transition-all duration-300
					${isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2.5'}
				`}
			>
				{/* Header */}
				<div className="px-5 py-4 font-semibold text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
					Switch Location
				</div>

				{/* Location List */}
				<div className="p-2 max-h-64 overflow-y-auto">
					{locations.map((location, index) => (
						<button
							key={location.id}
							onClick={() => handleLocationSelect(location)}
							className={`
								w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all cursor-pointer
								${
									selectedLocationId === location.id
										? 'bg-blue-50 dark:bg-blue-900/20'
										: 'hover:bg-gray-50 dark:hover:bg-gray-700'
								}
							`}
						>
							{/* Avatar */}
							<div
								className={`
									w-10 h-10 rounded-[10px] flex items-center justify-center font-bold text-sm text-white flex-shrink-0
									bg-gradient-to-br ${getLocationColor(index)}
								`}
							>
								{getInitials(profile?.businessName) || (
									<BuildingStorefrontIcon className="w-5 h-5" />
								)}
							</div>

							{/* Details */}
							<div className="flex-1 min-w-0">
								<div className="font-semibold text-gray-900 dark:text-gray-100 text-sm flex items-center gap-2">
									{profile?.businessName || 'Location'}
									{location.primary && (
										<span className="text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded font-medium">
											Primary
										</span>
									)}
								</div>
								<div className="text-gray-500 dark:text-gray-400 text-xs truncate">
									{formatAddress(location)}
								</div>
							</div>

							{/* Check Mark */}
							{selectedLocationId === location.id && (
								<CheckIcon className="w-5 h-5 text-[#667eea] flex-shrink-0" />
							)}
						</button>
					))}
				</div>

				{/* Divider */}
				<div className="border-t border-gray-200 dark:border-gray-700 mx-4" />

				{/* Add Location Button */}
				<div className="p-2">
					<button
						onClick={handleAddLocation}
						className="w-full flex items-center gap-3 p-3 rounded-xl text-left text-[#667eea] hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all cursor-pointer"
					>
						<div className="w-10 h-10 border-2 border-dashed border-[#667eea] rounded-[10px] flex items-center justify-center flex-shrink-0">
							<PlusIcon className="w-5 h-5" />
						</div>
						<span className="font-semibold text-sm">Add Another Location</span>
					</button>
				</div>
			</div>
		</div>
	);
};

export default LocationSwitcher;
